import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {InviteEmployeesDialog} from '@/components/invite-employees-dialog'
import {Hint} from '@/components/ui/hint'
import {HelpIcon} from '@/components/help-icon'
import {MicroLabel} from '@/components/period-selector'
import {ListEmptyState} from '@/components/section-card'
import {
  useStartAssessment,
  CHAPTER_STANDARD_COUNTS,
  previewChapter,
  type SgsstState,
} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {RISK_CLASS_ROMAN} from '@/lib/risk'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'

export function MissingRiskState({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  return (
    <div className="surface">
      <ListEmptyState
        page
        title={t('sgsst.missingRisk.title')}
        action={
          <Button asChild size="sm">
            <Link to={`/businesses/${businessId}/profile`}>{t('sgsst.missingRisk.cta')}</Link>
          </Button>
        }
      />
    </div>
  )
}

export function SetupCard({
  businessId,
  state,
  godReadOnly,
  canInvite,
}: {
  businessId: string
  state: SgsstState
  godReadOnly: boolean
  // Owner-only (identity-access-spec §1a.3): a responsable never mints or reads the registration link.
  canInvite: boolean
}) {
  const {t} = useTranslation()
  const [count, setCount] = useState(Math.max(state.active_employee_count, 1))
  const [error, setError] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)
  const start = useStartAssessment(businessId)
  const riskClass = state.risk_class as number
  const chapter = previewChapter(count || 1, riskClass)
  const ruleKey = riskClass >= 4 ? 'byRisk' : `bySize${chapter}`

  return (
    <div className="surface max-w-xl">
      <div className="surface-divider border-b px-6 py-4">
        <MicroLabel>{t('sgsst.setup.eyebrow')}</MicroLabel>
        <h2 className="mt-1 font-heading text-lg text-foreground">{t('sgsst.setup.title')}</h2>
      </div>
      <div className="space-y-5 p-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1.5">
            <MicroLabel>{t('sgsst.setup.riskLabel')}</MicroLabel>
            <p className="font-mono text-foreground">
              {t('business.risk.classValue', {roman: RISK_CLASS_ROMAN[riskClass]})}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="worker_count">{t('sgsst.setup.workersLabel')}</Label>
            <Input
              id="worker_count"
              type="number"
              min={1}
              value={count}
              disabled={godReadOnly}
              onChange={(e) => setCount(Number(e.target.value))}
            />
            <Hint>{t('sgsst.setup.workersHint', {count: state.active_employee_count})}</Hint>
          </div>
        </div>
        <div className="rounded-lg bg-secondary/60 px-4 py-3">
          <p className="font-mono text-sm font-medium text-foreground">
            {t('sgsst.chapter', {chapter})} · {t('sgsst.standardsCount', {count: CHAPTER_STANDARD_COUNTS[chapter]})}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t(`sgsst.setup.rule.${ruleKey}`)}</p>
        </div>
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3"
          data-testid="register-employees-note">
          <Hint className="flex items-center gap-1">
            {t('sgsst.setup.registerNote.lead')}
            <HelpIcon label={t('sgsst.setup.registerNote.tipLabel')} testId="register-employees-tip">
              {t('sgsst.setup.registerNote.tip')}
            </HelpIcon>
          </Hint>
          {canInvite && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="invite-employees-cta"
              onClick={() => setInviting(true)}>
              {t('sgsst.setup.registerNote.invite')}
            </Button>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {godReadOnly ? (
          <p className="text-xs text-muted-foreground">{t('sgsst.notStarted')}</p>
        ) : (
          <Button
            data-testid="nav-evaluacion"
            disabled={start.isPending || !count || count < 1}
            onClick={() =>
              start.mutate(count, {
                onSuccess: () => track('assessment_started', {business_id: businessId}),
                onError: (err) => setError(translateApiError(err)),
              })
            }>
            {t('sgsst.setup.start')}
          </Button>
        )}
        {canInvite && (
          <InviteEmployeesDialog
            businessId={businessId}
            canEdit={!godReadOnly}
            open={inviting}
            onOpenChange={setInviting}
          />
        )}
      </div>
    </div>
  )
}
